import { useState } from 'react';

import { Button, Stack } from '@mui/material';
import { getLoggedInAs, loginAsUser } from '@realadvisor/auth-client';
import { Undo2 } from 'lucide-react';
import { Outlet } from 'react-router-dom';

import { TopbarProvider } from '@app/LegacyBoundaries';
import { LanguageMenu } from '@modules/onboarding/OnboardingTopMenu';
import { GatewayLanguageMenuContext } from '@shared/gatewayToolbar';
import { useLocale } from '@shared/hooks/locale';

/**
 * Minimal layout for "chinese wall" routes: /claim, /claim-agency,
 * /takedown, /takedown-agency, /plans.
 *
 * These routes block access to the dashboard and share no CRM chrome
 * (no sidebar, no top navigation). TopbarProvider is included so that
 * legacy components like <TopbarTitle> can still set the page title.
 * Without the sidebar, an admin logged in as someone else would lose the
 * account menu's way back, so it is repeated here. Pages can add a language
 * switcher next to it with useGatewayLanguageMenu().
 */
export const GatewayLayout = () => {
  const { t } = useLocale();
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const loggedInAs = getLoggedInAs() != null;
  return (
    <TopbarProvider>
      <GatewayLanguageMenuContext.Provider value={setShowLanguageMenu}>
        {(loggedInAs || showLanguageMenu) && (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              position: 'fixed',
              top: 16,
              right: 16,
              zIndex: 2,
              alignItems: 'center',
            }}
          >
            {loggedInAs && (
              <Button
                variant="outlined"
                startIcon={<Undo2 size={16} />}
                onClick={() => loginAsUser({ user_id: null })}
                sx={{ bgcolor: 'background.paper' }}
              >
                {t('Back to my account')}
              </Button>
            )}
            {showLanguageMenu && <LanguageMenu />}
          </Stack>
        )}
        <Outlet />
      </GatewayLanguageMenuContext.Provider>
    </TopbarProvider>
  );
};
